"use client";

import { useEffect, useRef, useState } from "react";
import type { PreviewFilm } from "@/lib/previewCapture";

/* ────────────────────────────────────────────────────────────────────────────
 *  ScoreFilm — a score being made, recorded from the app.
 *
 *  The film is Playwright's recording of the capture session (scripts/
 *  capture-product-sidebar.ts, cutFilm): the address typed into the app's
 *  own search bar, the map landing, the registers answering, the verdict,
 *  the Location layer opened with the map answering it. The scan is re-timed
 *  to a smooth two seconds — the cascade takes 60–90 s — and the chapter
 *  strip says "sped up"; every frame is still a real frame of the recording.
 *
 *  It is DARK ONLY and framed as film (`data-theme="dark"`, the exemption
 *  §Frontend gotchas grants to photographs): the cascade runs once per
 *  session, so unlike the stills it cannot be re-shot in the other theme —
 *  and a second session would re-score the property (see the capture script
 *  on why one session is the whole point).
 *
 *  Plays muted when ~half in view, pauses out of view, loops. The chapter
 *  strip lights as the film passes each moment and seeks on click, so a
 *  reader can jump straight to the verdict. Reduced motion never autoplays:
 *  the poster stands with a play control, and the strip is still the summary.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface FilmChapter {
  key: keyof PreviewFilm["chapters"];
  label: string;
  detail: string;
}

export default function ScoreFilm({
  src,
  poster,
  film,
  chapters,
  playLabel,
}: {
  src: string;
  poster: string;
  film: PreviewFilm;
  chapters: FilmChapter[];
  playLabel: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const wrap = wrapRef.current;
    if (!video || !wrap) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && video.preload !== "auto") video.preload = "auto";
          if (reduce) continue;
          if (e.intersectionRatio >= 0.5) void video.play().catch(() => {});
          else if (!e.isIntersecting) video.pause();
        }
      },
      { rootMargin: "240px 0px", threshold: [0, 0.5] }
    );
    io.observe(wrap);
    return () => io.disconnect();
  }, []);

  const times = chapters.map((c) => film.chapters[c.key]);

  const onTime = () => {
    const video = videoRef.current;
    if (!video) return;
    const t = video.currentTime;
    let i = 0;
    for (let k = 0; k < times.length; k++) if (t >= times[k] - 0.05) i = k;
    if (i !== active) setActive(i);
  };

  const seek = (i: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = times[i];
    setActive(i);
    void video.play().catch(() => {});
  };

  return (
    <div ref={wrapRef}>
      {/* The bezel is the film's own dark, whatever the page wears — the
          footage is a fixed image, like the hero plates. */}
      <div
        data-theme="dark"
        className="relative rounded-2xl border border-edge bg-page p-1.5 sm:p-2 shadow-[0_40px_120px_-45px_rgb(0_0_0/0.6)]"
      >
        {/* The recording is the capture viewport, 1400×1276 CSS px (the
            panel's full 1276 px height) — see VIEWPORT in the script. */}
        <div className="relative overflow-hidden rounded-xl bg-page" style={{ aspectRatio: "1400 / 1276" }}>
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            muted
            playsInline
            loop
            preload="none"
            onTimeUpdate={onTime}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* One control, for reduced motion and for anyone whose browser
              refused the autoplay — otherwise the film is its own control. */}
          {!playing && (
            <button
              type="button"
              onClick={() => void videoRef.current?.play().catch(() => {})}
              className="absolute inset-0 flex items-center justify-center group"
              aria-label={playLabel}
            >
              <span className="inline-flex items-center gap-2.5 rounded-full bg-page/80 backdrop-blur px-5 py-2.5 text-[0.85rem] font-semibold text-ink border border-edge-strong group-hover:bg-page transition-colors">
                <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                  <path d="M4 2.5v11l9-5.5z" />
                </svg>
                {playLabel}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* ── Chapters. Lit by the film's clock; click to seek. ── */}
      <ol className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4" aria-label="Chapters">
        {chapters.map((c, i) => {
          const lit = i === active;
          return (
            <li key={c.key}>
              <button
                type="button"
                onClick={() => seek(i)}
                aria-current={lit ? "step" : undefined}
                className="group w-full text-left"
              >
                <span
                  aria-hidden
                  className="block h-px w-full mb-3 transition-colors duration-300"
                  style={{ background: lit ? "rgb(var(--c-accent))" : "rgb(var(--c-edge-strong))" }}
                />
                <span className="font-data text-[0.62rem] uppercase tracking-[0.18em] text-ink-muted">
                  <span className={lit ? "text-accent-ink" : ""}>0{i + 1}</span>
                  <span aria-hidden className="mx-1.5">·</span>
                  {c.label}
                </span>
                <span className={`block mt-1 text-[0.88rem] leading-snug transition-colors ${lit ? "text-ink" : "text-ink-2 group-hover:text-ink"}`}>
                  {c.detail}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
